import { Button, Flex, Image, Tag } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { REPORT_IDS_KEY, REPORT_INDEX_KEY } from '.';
import { ReportDetail } from '@/types/report';
import { useQuery } from '@tanstack/react-query';
import { getReportDetail } from '@/api/report';
import { getArtworkDetail } from '@/api/contentReview';
import dayjs from 'dayjs';
import { FORMAT_TIME } from '@/types/common';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { useAtomValue } from 'jotai';

interface ViewItemProps {
  reportDetail?: ReportDetail;
}

const ViewItem = ({ reportDetail }: ViewItemProps) => {
  const isMobile = useAtomValue(isMobileAtom);

  const labelStyle: React.CSSProperties = {
    fontWeight: 'bold',
    padding: isMobile ? 0 : '0 10px',
    width: 300,
    flexShrink: 0,
  };

  const { data: artworkDetail } = useQuery({
    queryKey: ['artworkDetail', reportDetail?.artworkId],
    queryFn: async () => {
      if (!reportDetail?.artworkId) {
        return;
      }
      const resp = await getArtworkDetail(reportDetail.artworkId);
      return resp.data;
    },
    enabled: !!reportDetail?.artworkId,
    refetchOnWindowFocus: false,
  });

  const renderTags = useCallback((tags?: string) => {
    if (!tags) {
      return '-';
    }
    tags = tags.replace(/[\[\]{}]/g, '');

    if (tags.includes(',')) {
      return tags
        .split(',')
        .map((t) => t.trim())
        .join(', ');
    }

    return '-';
  }, []);

  const renderImage = (imageUrlList?: string[]) => {
    if (!imageUrlList) {
      return [];
    }
    return imageUrlList.map((url) => (
      <Image key={url} src={url} width={isMobile ? 100 : 150} />
    ));
  };

  return (
    <div>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Name</div>
        <div>
          {reportDetail?.reportUserNickName || reportDetail?.reportUserName}
        </div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Supp ID</div>
        <div>{reportDetail?.userNumber}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Report ID</div>
        <div>{reportDetail?.reportId || '-'}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>The Report ID</div>
        <div>{reportDetail?.theReportUserId || '-'}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Artwork ID</div>
        <div>{artworkDetail?.artworkId || '-'}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Artwork Title</div>
        <div>{artworkDetail?.title || '-'}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Artwork Descriptions</div>
        <div style={{ maxWidth: 500, wordWrap: 'break-word' }}>
          {artworkDetail?.description || '-'}
        </div>
      </Flex>
      {/* <Flex align="center" gap="middle">
          <div style={labelStyle}>Artwork Category I</div>
          <div>xxxxxx</div>
        </Flex> */}
      {/* <Flex align="center" gap="middle">
          <div style={labelStyle}>Artwork Category II</div>
          <div>xxxxxx</div>
        </Flex> */}
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Tags</div>
        <div>{renderTags(artworkDetail?.tags)}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Publish Time</div>
        <div>{dayjs(artworkDetail?.createTime).format(FORMAT_TIME) || '-'}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Artist Name</div>
        <div>
          <Tag color="blue">Ongoing</Tag>
        </div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Artist ID</div>
        <div>
          <Tag color="blue">Ongoing</Tag>
        </div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>Gallery ID</div>
        <div>
          <Tag color="blue">Ongoing</Tag>
        </div>
      </Flex>
      <Flex style={{ overflowX: 'auto', marginTop: 10 }}>
        <Image.PreviewGroup>
          {renderImage(artworkDetail?.imageUrlList)}
        </Image.PreviewGroup>
      </Flex>
    </div>
  );
};

const ViewPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentIndex, setCurrentIndex] = useState(
    parseInt(searchParams.get(REPORT_INDEX_KEY) || '0')
  );
  const reportIdsRef = useRef<string[]>([]);
  const [reportId, setReportId] = useState<number>();

  const { data: reportDetail } = useQuery({
    queryKey: ['reportDetail', reportId],
    queryFn: async () => {
      if (!reportId) {
        return;
      }
      const resp = await getReportDetail(reportId);
      return resp.data;
    },
    enabled: !!reportId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const ids = searchParams.get(REPORT_IDS_KEY)?.split(',') || [];
    if (!reportIdsRef.current.length && ids.length) {
      reportIdsRef.current = ids;
      setReportId(parseInt(ids[currentIndex]));
    }
  }, [currentIndex, searchParams]);

  const handlePrevClick = useCallback(() => {
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    const prevId = reportIdsRef.current[newIndex];
    if (prevId) {
      setReportId(parseInt(prevId));
      const params = new URLSearchParams(searchParams);
      params.set(REPORT_INDEX_KEY, newIndex.toString());
      setSearchParams(params);
    }
  }, [currentIndex, searchParams, setSearchParams]);

  const handleNextClick = useCallback(() => {
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    const nextId = reportIdsRef.current[newIndex];
    if (nextId) {
      setReportId(parseInt(nextId));
      const params = new URLSearchParams(searchParams);
      params.set(REPORT_INDEX_KEY, newIndex.toString());
      setSearchParams(params);
    }
  }, [currentIndex, searchParams, setSearchParams]);

  const handleCancel = () => {
    navigate('/report');
  };

  return (
    <>
      <div className="report-view-container">
        <div className="pagination-btn-container">
          <Button
            type="primary"
            className="pagination-btn-left"
            disabled={currentIndex === 0}
            onClick={handlePrevClick}
            shape="circle"
          >
            &lt;
          </Button>
        </div>

        <div className="report-view-item-container">
          <ViewItem reportDetail={reportDetail} />
        </div>

        <div className="pagination-btn-container">
          <Button
            type="primary"
            className="pagination-btn-right"
            disabled={currentIndex === reportIdsRef.current.length - 1}
            onClick={handleNextClick}
            shape="circle"
          >
            &gt;
          </Button>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Button onClick={handleCancel}>CANCEL</Button>
      </div>
    </>
  );
};

export default ViewPage;
